import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, TextField, IconButton, Avatar, Chip, Tooltip,
  CircularProgress, Stack, Alert, Button, ToggleButtonGroup, ToggleButton,
  Drawer, List, ListItemButton, ListItemText, Divider, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, alpha, useTheme,
} from '@mui/material';
import {
  Send, SmartToy, Person, Stop, AutoAwesome,
  AddComment, ContentCopy, Refresh, AttachFile, Close, Visibility, TextFields,
  DeleteSweep, History, MoreVert, Delete, Edit, Image as ImageIcon, InsertDriveFile, Search,
  Mic, CheckCircle,
} from '@mui/icons-material';
import AgentResultTable from '../components/agent/AgentResultTable';
import AgentDraftCard from '../components/agent/AgentDraftCard';
import SafeHtml from '../components/agent/SafeHtml';
import { markdownToHtml } from '../lib/markdown';
import ThinkingSteps from '../components/agent/ThinkingSteps';
import { streamAgentChat, uploadChatAttachment, AgentEvent } from '../lib/agentApi';
import { useVoiceInput } from '../lib/useVoiceInput';
import { useChatThreads } from '../lib/useChatThreads';
import type { ChatMessage, Attachment, AgentStep, DraftBlock } from '../lib/chatTypes';
import { formatDate } from '../lib/dateUtils';

const MAX_TEXT_CHARS = 8000;
const MAX_IMAGE_BYTES = 4 * 1024 * 1024; // 4MB preview cap
const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20MB upload cap for non-image files

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.readAsDataURL(file);
  });

// Update the last step (the one currently running) with a status/result.
const markLastStep = (steps: AgentStep[] | undefined, patch: Partial<AgentStep>): AgentStep[] => {
  if (!steps || steps.length === 0) return steps || [];
  const next = [...steps];
  next[next.length - 1] = { ...next[next.length - 1], ...patch };
  return next;
};

const AssistantAnswer: React.FC<{ text: string; html?: string | null }> = ({ text, html }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'preview' | 'text'>('preview');
  // While streaming (no polished HTML yet) render the raw text as Markdown so
  // tables/bold/lists look right instead of raw pipes and asterisks.
  if (!html) return <SafeHtml html={markdownToHtml(text)} />;
  return (
    <Box>
      <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}
        sx={{ mb: 1, '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: '0.7rem' } }}>
        <ToggleButton value="preview"><Visibility fontSize="small" sx={{ mr: 0.5 }} />{t('agent.preview')}</ToggleButton>
        <ToggleButton value="text"><TextFields fontSize="small" sx={{ mr: 0.5 }} />{t('agent.plainText')}</ToggleButton>
      </ToggleButtonGroup>
      {mode === 'preview' ? <SafeHtml html={html} /> : <SafeHtml html={markdownToHtml(text)} />}
    </Box>
  );
};

// Renders an attachment chip / thumbnail (read-only, on a message or composer).
const AttachmentView: React.FC<{ att: Attachment; onRemove?: () => void }> = ({ att, onRemove }) => {
  if (att.kind === 'image' && att.dataUrl) {
    return (
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Box component="img" src={att.dataUrl} alt={att.name}
          sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }} />
        {onRemove && (
          <IconButton size="small" onClick={onRemove}
            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', border: 1, borderColor: 'divider', p: 0.25 }}>
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
    );
  }
  return (
    <Chip
      icon={att.kind === 'image' ? <ImageIcon fontSize="small" /> : <InsertDriveFile fontSize="small" />}
      label={att.name}
      onDelete={onRemove}
      deleteIcon={onRemove ? <Close /> : undefined}
      variant="outlined"
      size="small"
    />
  );
};

// Collapse duplicate result tables: when the agent refines a query and re-runs
// against the same entity, keep only the last (most refined) table per entity.
const dedupeResults = (results: ChatMessage['results']): NonNullable<ChatMessage['results']> => {
  const list = results || [];
  const lastIdx = new Map<string, number>();
  list.forEach((r, i) => lastIdx.set(r.entity ?? `__null_${i}`, i));
  return list.filter((r, i) => lastIdx.get(r.entity ?? `__null_${i}`) === i);
};

const AIAgentPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const {
    threads, activeId, activeMessages, loading: historyLoading, newThread, selectThread, deleteThread, renameThread, clearActive, setActiveMessages,
  } = useChatThreads();
  const [historyQuery, setHistoryQuery] = useState('');

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  // Live partial speech shown as a ghost preview while the user is still talking.
  const [interim, setInterim] = useState('');

  // Text already in the composer when dictation started. The voice session's
  // text is layered on top of this, so a Whisper correction can REPLACE the
  // provisional live text instead of appending it twice.
  const voiceBaseRef = useRef('');

  // Map the hook's error codes to friendly, localized messages.
  const voiceErrorText = (code: string): string => {
    const map: Record<string, string> = {
      MIC_DENIED: t('agent.micDenied'),
      MIC_NOT_FOUND: t('agent.micNotFound'),
      MIC_ERROR: t('agent.micError'),
      RECORDER_ERROR: t('agent.micError'),
      VOICE_UNSUPPORTED: t('agent.voiceUnsupported'),
      TOO_SHORT: t('agent.tooShort'),
      NO_SPEECH: t('agent.noSpeech'),
      TRANSCRIBE_FAILED: t('agent.transcribeFailed'),
      TRANSCRIBE_TIMEOUT: t('agent.transcribeTimeout'),
    };
    return map[code] || code;
  };

  // Voice dictation (hybrid): the live engine fills the box as you speak, then
  // Whisper's more accurate transcript replaces it. Never auto-sent — the admin
  // reviews before running, so a mis-hearing can't execute a wrong query.
  const voice = useVoiceInput({
    language: isRTL ? 'ar' : 'en',
    onResult: (text) => {
      setVoiceError(null);
      setInterim('');
      const base = voiceBaseRef.current.trim();
      setInput(base ? `${base} ${text}` : text);
    },
    onInterim: (text) => setInterim(text),
    onError: (code) => {
      setInterim('');
      setVoiceError(voiceErrorText(code));
    },
  });

  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Mirror of the streaming conversation so we persist on every tick.
  const liveRef = useRef<ChatMessage[]>(activeMessages);

  const suggestions: string[] = t('agent.suggestions', { returnObjects: true }) as unknown as string[];

  const theme = useTheme();
  // An error is only *shown* once recording/transcribing has settled, so the
  // strip never fights between a live state and a stale error.
  const showVoiceError = !!voiceError && !voice.recording && !voice.transcribing;

  // Keep the view pinned to the latest content. Uses instant scroll so it stays
  // glued to the bottom during rapid token streaming (smooth can't keep up).
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeMessages, loading]);

  const stop = () => { abortRef.current?.abort(); abortRef.current = null; setLoading(false); };

  const copyText = (msg: ChatMessage) => navigator.clipboard?.writeText(msg.content || '');

  // ── File handling ───────────────────────────────────────────────
  const fileToAttachment = async (file: File): Promise<Attachment | null> => {
    const isImage = file.type.startsWith('image/');
    const isTextual = /\.(txt|csv|json|md|log)$/i.test(file.name) || file.type.startsWith('text/');
    if (isImage) {
      if (file.size > MAX_IMAGE_BYTES) return { name: file.name, kind: 'image', size: file.size };
      const dataUrl = await readAsDataUrl(file);
      return { name: file.name, kind: 'image', dataUrl, size: file.size };
    }
    if (isTextual) {
      const text = (await file.text()).slice(0, MAX_TEXT_CHARS);
      return { name: file.name, kind: 'text', text, size: file.size };
    }
    // Other files (PDF, docx, …): keep a data URL so we can upload to storage.
    if (file.size <= MAX_FILE_BYTES) {
      const dataUrl = await readAsDataUrl(file);
      return { name: file.name, kind: 'other', dataUrl, size: file.size };
    }
    return { name: file.name, kind: 'other', size: file.size };
  };

  const addFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    const next = (await Promise.all(arr.map(fileToAttachment))).filter(Boolean) as Attachment[];
    setAttachments((prev) => [...prev, ...next]);
  };

  const handleAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) await addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files || []);
    if (files.length) { e.preventDefault(); await addFiles(files); }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files?.length) await addFiles(e.dataTransfer.files);
  };

  // ── Turn execution ──────────────────────────────────────────────
  const runTurn = async (outgoing: ChatMessage[]) => {
    const withAssistant: ChatMessage[] = [...outgoing, { role: 'assistant', content: '', html: null, results: [], runningSql: null }];
    liveRef.current = withAssistant;
    setActiveMessages(withAssistant);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    // Upload any attached media/files to storage so the agent gets real URLs it
    // can put into a draft (e.g. a news cover image or a document attachment).
    for (const m of outgoing) {
      if (m.role !== 'user' || !m.attachments) continue;
      for (const a of m.attachments) {
        if (a.url || a.kind === 'text' || !a.dataUrl) continue;
        try { a.url = await uploadChatAttachment(a.name, a.dataUrl); } catch { /* non-fatal */ }
      }
    }

    // Build the wire history (inline text-file contents + uploaded file URLs).
    const history = outgoing.map((m) => {
      let content = m.content;
      const textFiles = m.attachments?.filter((a) => a.kind === 'text' && a.text) || [];
      const media = m.attachments?.filter((a) => a.kind !== 'text') || [];
      if (textFiles.length) content += '\n\n' + textFiles.map((a) => `--- ${a.name} ---\n${a.text}`).join('\n\n');
      if (media.length) {
        content += '\n\n' + media.map((a) =>
          a.url
            ? `[Uploaded ${a.kind === 'image' ? 'image' : 'file'} "${a.name}" — storage URL: ${a.url}]`
            : `[Attached: ${a.name}]`,
        ).join('\n');
      }
      return { role: m.role, content: content.trim() };
    });

    const updateAssistant = (fn: (m: ChatMessage) => ChatMessage) => {
      const next = [...liveRef.current];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant') next[next.length - 1] = fn(last);
      liveRef.current = next;
      setActiveMessages(next);
    };

    const handleEvent = (event: AgentEvent) => {
      switch (event.type) {
        case 'token': updateAssistant((m) => ({ ...m, content: m.content + event.text })); break;
        case 'html': updateAssistant((m) => ({ ...m, html: event.html })); break;
        case 'tool_start':
          // Record a new running step (shown in the collapsible steps panel).
          updateAssistant((m) => ({
            ...m, runningSql: event.sql,
            steps: [...(m.steps || []), { sql: event.sql, status: 'running' as const, at: Date.now() }],
          }));
          break;
        case 'tool_result':
          updateAssistant((m) => {
            // Mark the matching running step done; only keep NON-EMPTY tables as
            // prominent results (empty queries stay in the steps panel only).
            const steps = markLastStep(m.steps, { status: 'done', rowCount: event.rowCount });
            const results = event.rowCount > 0
              ? [...(m.results || []), {
                  entity: event.entity, rows: event.rows, columns: event.columns,
                  rowCount: event.rowCount, route: event.route, sql: event.sql,
                }]
              : m.results;
            return { ...m, runningSql: null, steps, results };
          });
          break;
        case 'tool_error':
          updateAssistant((m) => ({
            ...m, runningSql: null,
            steps: markLastStep(m.steps, { status: 'error', error: event.error }),
          }));
          break;
        case 'draft':
          updateAssistant((m) => ({
            ...m,
            drafts: [
              ...(m.drafts || []),
              {
                entity: event.entity, action: event.action, id: event.id,
                fields: event.fields, summary: event.summary, route: event.route,
                status: 'pending' as const,
              },
            ],
          }));
          break;
        case 'error': updateAssistant((m) => ({ ...m, error: event.error })); break;
        case 'done': break;
      }
    };

    try {
      await streamAgentChat(history, handleEvent, controller.signal);
    } catch (err) {
      if (!controller.signal.aborted) updateAssistant((m) => ({ ...m, error: err instanceof Error ? err.message : 'Error' }));
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const send = (text: string) => {
    const trimmed = text.trim();
    if ((!trimmed && attachments.length === 0) || loading) return;
    const userMsg: ChatMessage = { role: 'user', content: trimmed, attachments: attachments.length ? attachments : undefined };
    setInput('');
    setAttachments([]);
    setEditingIdx(null);
    runTurn([...activeMessages, userMsg]);
  };

  // Update a proposed draft's status (applied/cancelled) on a specific message.
  const updateDraft = (msgIdx: number, draftIdx: number, patch: Partial<DraftBlock>) => {
    const next = activeMessages.map((m, i) =>
      i === msgIdx && m.drafts
        ? { ...m, drafts: m.drafts.map((d, j) => (j === draftIdx ? { ...d, ...patch } : d)) }
        : m,
    );
    liveRef.current = next;
    setActiveMessages(next);
  };

  const regenerate = () => {
    if (loading) return;
    const lastUserIdx = [...activeMessages].map((m) => m.role).lastIndexOf('user');
    if (lastUserIdx === -1) return;
    runTurn(activeMessages.slice(0, lastUserIdx + 1));
  };

  // Edit & resend: load a prior user message into the composer, truncate from there.
  const startEdit = (idx: number) => {
    const msg = activeMessages[idx];
    if (msg.role !== 'user') return;
    setInput(msg.content);
    setAttachments(msg.attachments || []);
    setEditingIdx(idx);
  };
  const submitEdit = () => {
    if (editingIdx === null) return;
    const trimmed = input.trim();
    if (!trimmed && attachments.length === 0) return;
    const edited: ChatMessage = { role: 'user', content: trimmed, attachments: attachments.length ? attachments : undefined };
    const base = activeMessages.slice(0, editingIdx);
    setInput(''); setAttachments([]); setEditingIdx(null);
    runTurn([...base, edited]);
  };
  const cancelEdit = () => { setEditingIdx(null); setInput(''); setAttachments([]); };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      editingIdx !== null ? submitEdit() : send(input);
    }
    if (e.key === 'Escape' && editingIdx !== null) cancelEdit();
  };

  const startNewChat = () => { stop(); setInput(''); setAttachments([]); setEditingIdx(null); newThread(); };
  const clearChat = () => { stop(); setInput(''); setAttachments([]); setEditingIdx(null); clearActive(); };

  const showEmptyState = activeMessages.length === 0;

  const hq = historyQuery.trim().toLowerCase();
  const filteredThreads = hq
    ? threads.filter((th) =>
        (th.title || '').toLowerCase().includes(hq) ||
        th.messages.some((m) => (m.content || '').toLowerCase().includes(hq)),
      )
    : threads;

  return (
    <Box sx={{ height: 'calc(100vh - 96px)', display: 'flex', flexDirection: 'column' }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}><AutoAwesome fontSize="small" /></Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{t('agent.title')}</Typography>
          <Typography variant="caption" color="text.secondary">{t('agent.subtitle')}</Typography>
        </Box>
        <Tooltip title={t('agent.history')}>
          <IconButton onClick={() => setHistoryOpen(true)}><History /></IconButton>
        </Tooltip>
        <Tooltip title={t('agent.clear')}>
          <span>
            <IconButton onClick={clearChat} disabled={showEmptyState && !loading}><DeleteSweep /></IconButton>
          </span>
        </Tooltip>
        <Button
          size="small"
          variant="contained"
          onClick={startNewChat}
          startIcon={<AddComment fontSize="small" />}
          sx={{ minWidth: { xs: 40, sm: 'auto' }, px: { xs: 1, sm: 2 }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } } }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('agent.newChat')}</Box>
        </Button>
      </Box>

      {/* Messages */}
      <Box ref={scrollRef} sx={{
        flex: 1, overflowY: 'auto', px: { xs: 0, sm: 1 }, py: 1, position: 'relative',
        ...(dragOver && { outline: (theme) => `2px dashed ${theme.palette.primary.main}`, borderRadius: 2, bgcolor: 'action.hover' }),
      }}>
        {dragOver && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, pointerEvents: 'none' }}>
            <Chip icon={<AttachFile />} label={t('agent.dropHere')} color="primary" />
          </Box>
        )}
        {showEmptyState ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}><SmartToy /></Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('agent.emptyTitle')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mt: 0.5 }}>{t('agent.emptyHint')}</Typography>
            </Box>
            <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="center" sx={{ maxWidth: 560 }}>
              {Array.isArray(suggestions) && suggestions.map((s, i) => (
                <Chip key={i} label={s} onClick={() => send(s)} variant="outlined" sx={{ cursor: 'pointer' }} />
              ))}
            </Stack>
          </Box>
        ) : (
          <Stack spacing={2}>
            {activeMessages.map((msg, idx) => {
              const isLastAssistant = msg.role === 'assistant' && idx === activeMessages.length - 1;
              const mine = msg.role === 'user';
              return (
                <Box key={idx} sx={{
                  display: 'flex',
                  // User messages pinned to the right, agent to the left (both RTL and LTR).
                  justifyContent: mine ? (isRTL ? 'flex-start' : 'flex-end') : (isRTL ? 'flex-end' : 'flex-start'),
                  '@keyframes msgIn': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
                  animation: 'msgIn 0.25s ease',
                }}>
                  <Box sx={{
                    display: 'flex', gap: 1.25, maxWidth: { xs: '92%', md: '85%' },
                    // Avatar on the user's side (right for user, left for agent).
                    flexDirection: (mine === isRTL) ? 'row' : 'row-reverse',
                  }}>
                  <Avatar sx={{ width: 30, height: 30, flexShrink: 0, bgcolor: msg.role === 'user' ? 'secondary.main' : 'primary.main' }}>
                    {msg.role === 'user' ? <Person fontSize="small" /> : <SmartToy fontSize="small" />}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: msg.role === 'user' ? 'action.hover' : 'background.paper' }}>
                      {msg.attachments && msg.attachments.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                          {msg.attachments.map((a, ai) => <AttachmentView key={ai} att={a} />)}
                        </Stack>
                      )}

                      {msg.role !== 'assistant' && (
                        <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.95rem' }}>{msg.content}</Typography>
                      )}

                      {/* 1) Result tables UP TOP (de-duplicated so a refined re-query doesn't show twice) */}
                      {dedupeResults(msg.results).map((r, ri) => (
                        <AgentResultTable key={ri} entity={r.entity} rows={r.rows} columns={r.columns} rowCount={r.rowCount} route={r.route} />
                      ))}

                      {/* Proposed add/edit drafts awaiting the admin's confirmation */}
                      {msg.drafts?.map((d, di) => (
                        <AgentDraftCard
                          key={di}
                          draft={d}
                          onApplied={(row) => updateDraft(idx, di, { status: 'applied', resultId: row?.id != null ? String(row.id) : undefined })}
                          onCancelled={() => updateDraft(idx, di, { status: 'cancelled' })}
                        />
                      ))}

                      {/* 3) Answer / summary text — rendered LAST, below the tables (Markdown) */}
                      {msg.role === 'assistant' && (msg.content || msg.html) && (
                        <Box sx={{ mt: (dedupeResults(msg.results).length > 0 || (msg.steps && msg.steps.length > 0)) ? 1.5 : 0 }}>
                          <AssistantAnswer text={msg.content} html={msg.html} />
                        </Box>
                      )}
                      {msg.role === 'assistant' && !msg.content && !msg.html && !loading && dedupeResults(msg.results).length > 0 && (
                        <Typography variant="body2" sx={{ mt: 1 }}>{t('agent.hereAreResults')}</Typography>
                      )}

                      {/* 4) Process ("what it's doing" — the queries) kept at the BOTTOM, collapsible */}
                      {msg.role === 'assistant' && msg.steps && msg.steps.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <ThinkingSteps steps={msg.steps} active={isLastAssistant && loading} />
                        </Box>
                      )}

                      {/* Persistent status at the very bottom: shows it's still working until done */}
                      {isLastAssistant && loading && !msg.error && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: 'text.secondary' }}>
                          <CircularProgress size={13} />
                          <Typography variant="caption">{t('agent.working')}</Typography>
                        </Box>
                      )}

                      {msg.error && <Alert severity="error" sx={{ mt: msg.content ? 1 : 0 }}>{msg.error}</Alert>}
                    </Paper>

                    {/* Per-message actions */}
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: msg.role === 'user' ? (isRTL ? 'flex-start' : 'flex-end') : (isRTL ? 'flex-end' : 'flex-start') }}>
                      {msg.role === 'user' && !loading && (
                        <Tooltip title={t('agent.edit')}>
                          <IconButton size="small" onClick={() => startEdit(idx)}><Edit sx={{ fontSize: 16 }} /></IconButton>
                        </Tooltip>
                      )}
                      {msg.role === 'assistant' && (msg.content || msg.html) && (
                        <>
                          <Tooltip title={t('agent.copy')}>
                            <IconButton size="small" onClick={() => copyText(msg)}><ContentCopy sx={{ fontSize: 16 }} /></IconButton>
                          </Tooltip>
                          {isLastAssistant && !loading && (
                            <Tooltip title={t('agent.regenerate')}>
                              <IconButton size="small" onClick={regenerate}><Refresh sx={{ fontSize: 16 }} /></IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Composer attachments */}
      {attachments.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
          {attachments.map((a, i) => (
            <AttachmentView key={i} att={a} onRemove={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} />
          ))}
        </Stack>
      )}

      {/* Edit banner */}
      {editingIdx !== null && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip size="small" color="warning" icon={<Edit fontSize="small" />} label={t('agent.editing')} />
          <Button size="small" onClick={cancelEdit}>{t('agent.cancelEdit')}</Button>
        </Box>
      )}

      {/* ── Composer ────────────────────────────────────────────────────────
          One self-contained block. Voice status lives INSIDE it (attached strip
          at the top) and every control sits in a single cluster on the trailing
          edge, so nothing floats around the screen while dictating. */}
      <Paper variant="outlined" sx={{
        mt: 1, borderRadius: 2, overflow: 'hidden',
        borderColor: voice.recording ? 'error.main' : showVoiceError ? 'error.light' : 'divider',
        transition: 'border-color .2s',
      }}>
        {/* Status strip: recording / live words / transcribing / error */}
        {(voice.recording || voice.transcribing || showVoiceError) && (
          <Box sx={{
            px: 1.25, py: 0.75, display: 'flex', alignItems: 'center', gap: 1,
            borderBottom: 1, borderColor: 'divider',
            // Subtle red wash for errors, neutral for in-progress states.
            bgcolor: showVoiceError ? alpha(theme.palette.error.main, 0.08) : 'action.hover',
          }}>
            {voice.recording && (
              <>
                {/* Pulsing dot + timer + level bars: unambiguous "we are hearing you" */}
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0,
                  '@keyframes recDot': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
                  animation: 'recDot 1.1s ease-in-out infinite',
                }} />
                <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', color: 'error.main', fontWeight: 700, flexShrink: 0 }}>
                  {String(Math.floor(voice.seconds / 60)).padStart(2, '0')}:{String(voice.seconds % 60).padStart(2, '0')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.3, height: 14, flexShrink: 0 }}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Box key={i} sx={{
                      width: 3, borderRadius: 0.5, height: `${30 + i * 16}%`,
                      bgcolor: voice.level * 5 > i ? 'error.main' : 'action.disabledBackground',
                      transition: 'background-color .08s',
                    }} />
                  ))}
                </Box>
                <Typography variant="caption" sx={{
                  color: 'text.secondary', fontStyle: interim ? 'italic' : 'normal',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
                }}>
                  {interim || (voice.status === 'starting' ? t('agent.micStarting') : t('agent.listening'))}
                </Typography>
              </>
            )}
            {voice.transcribing && (
              <>
                <CircularProgress size={12} sx={{ flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{t('agent.polishing')}</Typography>
              </>
            )}
            {showVoiceError && (
              <>
                <Typography variant="caption" color="error" sx={{ flex: 1, minWidth: 0 }}>{voiceError}</Typography>
                <IconButton size="small" color="error" onClick={() => setVoiceError(null)} sx={{ p: 0.25 }}>
                  <Close sx={{ fontSize: 15 }} />
                </IconButton>
              </>
            )}
          </Box>
        )}

        {/* Input row */}
        <Box sx={{ p: 1, display: 'flex', alignItems: 'flex-end', gap: 0.25 }}>
          <input ref={fileInputRef} type="file" hidden multiple onChange={handleAttach} accept=".txt,.csv,.json,.md,.log,image/*" />
          <Tooltip title={t('agent.attach')}>
            <span>
              <IconButton onClick={() => fileInputRef.current?.click()} disabled={loading || voice.recording} size="small">
                <AttachFile />
              </IconButton>
            </span>
          </Tooltip>

          <TextField
            fullWidth multiline maxRows={6} size="small"
            placeholder={voice.recording ? t('agent.speakNow') : t('agent.placeholder')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{ px: 1, py: 0.5 }}
          />

          {/* Control cluster — always the same spot, grouped tightly together */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            {voice.recording ? (
              <>
                <Tooltip title={t('agent.finishRecording')}>
                  <IconButton color="success" size="small" onClick={voice.stop}><CheckCircle /></IconButton>
                </Tooltip>
                {/* Neutral (not red) so it doesn't compete with the recording indicator */}
                <Tooltip title={t('agent.discardRecording')}>
                  <IconButton size="small" onClick={voice.cancel}><Close /></IconButton>
                </Tooltip>
              </>
            ) : voice.transcribing ? (
              <Tooltip title={t('dialog.cancel')}>
                <IconButton size="small" onClick={voice.cancel}><Close /></IconButton>
              </Tooltip>
            ) : (
              voice.supported && (
                <Tooltip title={t('agent.dictate')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => { voiceBaseRef.current = input; voice.start(); }}
                      disabled={loading}
                    >
                      <Mic />
                    </IconButton>
                  </span>
                </Tooltip>
              )
            )}

            {loading ? (
              <Tooltip title={t('agent.stop')}>
                <IconButton color="error" size="small" onClick={stop}><Stop /></IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={editingIdx !== null ? t('agent.resend') : t('agent.send')}>
                <span>
                  <IconButton
                    color="primary" size="small"
                    onClick={() => (editingIdx !== null ? submitEdit() : send(input))}
                    disabled={voice.recording || voice.transcribing || (!input.trim() && attachments.length === 0)}
                  >
                    <Send sx={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
        {t('agent.readonlyNote')}
      </Typography>

      {/* History drawer */}
      <Drawer anchor={isRTL ? 'left' : 'right'} open={historyOpen} onClose={() => setHistoryOpen(false)}>
        <Box sx={{ width: { xs: 290, sm: 330 }, p: 1.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <History fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>{t('agent.history')}</Typography>
            <Button size="small" variant="contained" startIcon={<AddComment fontSize="small" />} onClick={() => { startNewChat(); setHistoryOpen(false); }}>
              {t('agent.newChat')}
            </Button>
          </Box>

          {threads.length > 0 && (
            <TextField
              size="small" fullWidth value={historyQuery}
              onChange={(e) => setHistoryQuery(e.target.value)}
              placeholder={t('agent.searchChats')}
              sx={{ mb: 1 }}
              InputProps={{ startAdornment: (<Search fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} />) }}
            />
          )}
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ flex: 1, overflowY: 'auto', mx: -0.5, px: 0.5 }}>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
            ) : threads.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>{t('agent.noHistory')}</Typography>
            ) : filteredThreads.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>{t('agent.noMatch')}</Typography>
            ) : (
              <List dense>
                {filteredThreads.map((th) => (
                  <ListItemButton key={th.id} selected={th.id === activeId}
                    onClick={() => { selectThread(th.id); setHistoryOpen(false); }}
                    sx={{ borderRadius: 1.5, mb: 0.5, transition: 'background-color .15s' }}>
                    <ListItemText
                      primary={th.title || t('agent.untitled')}
                      secondary={formatDate(new Date(th.updatedAt).toISOString(), isRTL ? 'ar' : 'en', 'short')}
                      primaryTypographyProps={{ noWrap: true, fontSize: '0.85rem', fontWeight: th.id === activeId ? 600 : 400 }}
                      secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                    <IconButton size="small" edge="end" onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, id: th.id }); }}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Per-thread menu */}
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => {
          if (menuAnchor) {
            const th = threads.find((t) => t.id === menuAnchor.id);
            setRenaming({ id: menuAnchor.id, title: th?.title || '' });
          }
          setMenuAnchor(null);
        }}>
          <Edit fontSize="small" sx={{ mr: 1 }} />{t('agent.renameThread')}
        </MenuItem>
        <MenuItem onClick={() => { if (menuAnchor) deleteThread(menuAnchor.id); setMenuAnchor(null); }} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />{t('agent.deleteThread')}
        </MenuItem>
      </Menu>

      {/* Rename dialog */}
      <Dialog open={!!renaming} onClose={() => setRenaming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('agent.renameThread')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" sx={{ mt: 1 }}
            value={renaming?.title || ''}
            placeholder={t('agent.renamePlaceholder')}
            onChange={(e) => setRenaming((r) => (r ? { ...r, title: e.target.value } : r))}
            onKeyDown={(e) => { if (e.key === 'Enter' && renaming) { renameThread(renaming.id, renaming.title); setRenaming(null); } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>{t('dialog.cancel')}</Button>
          <Button variant="contained" onClick={() => { if (renaming) { renameThread(renaming.id, renaming.title); setRenaming(null); } }}>
            {t('dialog.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIAgentPage;
